import { formatTimeWithTimezone } from '@thyme/libs/src/date/formatting'
import { DateTime } from 'luxon'
import { storeToRefs } from 'pinia'
import { usePatientStore } from '@/legacy/store/modules/patient'
import { maybeAddTimezone } from '@/libs/date/date'
import { formatCleanDateTime } from '@/libs/format/format'

const DEFAULT_TIME_ZONE = 'US/Central'

/**
 * !! Requires Patient Store !!
 * Utility functions for date and timezone for the patient profile page
 */
export function setupDateTimeUtils() {
  const { patient } = storeToRefs(usePatientStore())
  /**
   * Converts a string representation of a date to a Date object in the member's timezone (if available)
   * uses the default timezone if not available
   * @param dateString The string representation of the date.
   */
  function stringToMemberDateTime(
    dateString: string | null | undefined
  ): DateTime | null {
    if (!dateString) return null

    const dateTime = DateTime.fromISO(maybeAddTimezone(dateString))
    if (patient.value?.timezone) {
      return dateTime.setZone(patient.value.timezone)
    }
    return dateTime.setZone(DEFAULT_TIME_ZONE)
  }

  /**
   * Converts a string representation of a date to a Date object in the local timezone.
   * @param dateString The string representation of the date.
   */
  function stringToLocalDateTime(
    dateString: string | null | undefined
  ): DateTime | null {
    if (!dateString) return null

    return DateTime.fromISO(maybeAddTimezone(dateString))
  }

  /**
   * Converts datestring to relevant DateTime object based on member timezone or local
   * @param dateString
   */
  function getDateTimeFromApi(
    dateString: string | null | undefined
  ): DateTime | null {
    return stringToMemberDateTime(dateString)
  }

  /**
   * converts datestring to relevant Date object based on member timezone or local
   * @param dateString
   */
  function getDateFromAPI(dateString: string | null | undefined): Date | null {
    return getDateTimeFromApi(dateString)?.toJSDate() ?? null
  }

  /**
   *
   * @param dateString
   */
  function getDisplayDateString(dateString: string | null | undefined): string {
    const dateTimeObj = getDateTimeFromApi(dateString)
    if (!dateTimeObj) return ''

    return formatCleanDateTime(dateTimeObj)
  }

  /**
   * get human readable time string including timezone
   * @param dateString
   * @param useLocal if true, uses local browser timezone; otherwise uses member timezone
   */
  function getDisplayTimeString(
    dateString: string | null | undefined,
    useLocal = false
  ): string {
    const timezone = useLocal ? null : getTimezone()
    return formatTimeWithTimezone(dateString, timezone)
  }

  /**
   * get human readable timezone string
   * @param useLocal if true, returns local browser timezone; otherwise returns member timezone
   */
  function getTimezoneString(useLocal = false): string {
    if (useLocal) {
      return DateTime.now().toFormat('ZZZZ')
    }
    if (patient.value?.timezone) {
      return DateTime.now().setZone(patient.value.timezone).toFormat('ZZZZ')
    }
    return DateTime.now().setZone(DEFAULT_TIME_ZONE).toFormat('ZZZZ')
  }

  /**
   * get IANA timezone string based on feature flag
   */
  function getTimezone(): string | null {
    if (patient.value?.timezone) {
      return patient.value.timezone
    }
    return DEFAULT_TIME_ZONE
  }

  return {
    getDateFromAPI,
    getDisplayDateString,
    getDisplayTimeString,
    getTimezoneString,
    getTimezone,
    stringToLocalDateTime,
  }
}
